import React, { useState, useEffect, useRef } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaTimes, FaUser, FaCheck, FaCheckDouble } from 'react-icons/fa';

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Read receipt component - WhatsApp/Telegram style
const ReadReceipt = ({ read, readAt }) => {
  if (read) {
    return (
      <span className="inline-flex items-center ml-1.5" title={readAt ? `Read at ${formatTime(readAt)}` : 'Read'}>
        <FaCheckDouble className="text-blue-300 text-xs" style={{ filter: 'brightness(1.3)' }} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center ml-1.5" title="Sent">
      <FaCheck className="text-gray-400 text-xs" />
    </span>
  );
};

const ChatWindow = ({ bookingId, onClose, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchBooking();
    fetchMessages();

    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(false);
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await api.get(`/api/bookings/${bookingId}`);
      setBooking(response.data.booking);
    } catch (error) {
      console.error('Fetch booking error:', error);
      toast.error('Failed to load booking details');
    }
  };

  const fetchMessages = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get(`/api/messages/booking/${bookingId}`);
      console.log('Fetched messages:', response.data.messages);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Fetch messages error:', error);
      console.error('Error details:', error.response?.data);
      if (showLoading) {
        toast.error('Failed to load messages');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await api.post('/api/messages', {
        bookingId,
        message: newMessage.trim()
      });

      // Add the new message to the list
      setMessages(prev => [...prev, response.data.data]);
      setNewMessage('');
      scrollToBottom();
      
      // Refresh messages to ensure consistency
      setTimeout(() => {
        fetchMessages(false);
      }, 500);
      
      toast.success('Message sent!');
    } catch (error) {
      console.error('Send message error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!booking) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const otherUser = (currentUser._id === booking.passenger._id || 
                     currentUser._id?.toString() === booking.passenger._id?.toString() ||
                     currentUser._id === booking.passenger._id?.toString() ||
                     currentUser._id?.toString() === booking.passenger._id) 
    ? booking.rider 
    : booking.passenger;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl h-[80vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {otherUser.photo ? (
              <img
                src={otherUser.photo}
                alt={otherUser.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center">
                <FaUser className="text-white" />
              </div>
            )}
            <div>
              <h3 className="font-semibold">{otherUser.name}</h3>
              <p className="text-xs text-blue-100">
                {currentUser._id === booking.passenger._id ? 'Rider' : 'Passenger'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-full text-gray-500">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.sender._id === currentUser._id || 
                                   message.sender._id?.toString() === currentUser._id?.toString();
              return (
                <div
                  key={message._id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      isOwnMessage
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    {!isOwnMessage && (
                      <p className="text-xs font-semibold mb-1 opacity-75">
                        {message.sender?.name || 'Unknown'}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                    <div className={`flex items-center justify-end mt-1 gap-1 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                      <p className="text-xs">
                        {formatTime(message.createdAt)}
                      </p>
                      {isOwnMessage && (
                        <ReadReceipt read={message.read} readAt={message.readAt} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaPaperPlane />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;

